/**
 * whiteboardDemoBoards — sample boards for whiteboard-import demo mode.
 *
 * Each demo board is a canned, pre-recognized WhiteboardScene (typed against
 * the real scene-graph contract) plus a synthetic "photo" of that scene drawn
 * as an SVG data URL. Demo mode injects the scene where the vision model's
 * output would normally land, so everything downstream — buildImportPlan, the
 * preview editor, commit, undo, and the reveal — is the exact live pipeline.
 * Only the camera and the vision API call are skipped, which is the point:
 * the demo needs no whiteboard, no photo, and no Claude key.
 *
 * The fixtures deliberately exercise the signifier grammar the importer
 * understands: sticky colors → categories, vertical position → altitude,
 * arrow styles and symbols → relationship types, a container → "contains",
 * and one low-confidence transcription → the human-review flag.
 */

import type { WhiteboardScene, WhiteboardNode, WhiteboardEdge } from './whiteboardImportService';

export interface DemoBoard {
  id: string;
  /** Short button label in the import dialog. */
  label: string;
  /** One-line tooltip: what this sample shows off. */
  description: string;
  scene: WhiteboardScene;
  /** Synthetic capture photo (SVG data URL) — the reveal's fog-lift backdrop. */
  photoDataUrl: string;
}

// ── Synthetic photo rendering ────────────────────────────────────────────────

/** Marker-ink and sticky-paper palette for the drawn "photo". */
const PHOTO_STICKY_FILL: Record<string, string> = {
  yellow: '#FBE38E', orange: '#F9C97C', red: '#F1A29B', pink: '#F3AECB',
  green: '#A9DFB4', light_green: '#C6EBCB', blue: '#A9C8F0', light_blue: '#C4E0F5',
  cyan: '#AEE6EC', purple: '#CDB4E8', violet: '#CDB4E8', gray: '#DDDDD6',
  white: '#FFFFFF', unknown: 'none',
};

const PHOTO_W = 1200;
const PHOTO_H = 800;
const NODE_W = 200;
const NODE_H = 92;
const INK = '#2A3140';

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Naive word wrap for the hand-written labels: up to `maxLines` short lines. */
function wrapText(text: string, maxChars: number, maxLines: number): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    if (!line) line = w;
    else if ((line + ' ' + w).length <= maxChars) line += ' ' + w;
    else {
      lines.push(line);
      line = w;
      if (lines.length === maxLines - 1) break;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  return lines;
}

function nodeCenter(n: WhiteboardNode): { cx: number; cy: number } {
  return { cx: 60 + n.position.x * (PHOTO_W - 120), cy: 60 + n.position.y * (PHOTO_H - 120) };
}

/** Trim an edge's endpoints back from the node centers so lines meet box borders. */
function trimSegment(x1: number, y1: number, x2: number, y2: number, trim: number) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  return { x1: x1 + ux * trim, y1: y1 + uy * trim, x2: x2 - ux * trim, y2: y2 - uy * trim };
}

/**
 * Draw the scene as a believable whiteboard photo: framed board, tilted
 * stickies with soft shadows, marker boxes, ink connectors with arrowheads,
 * and hand-writing-ish text. Pure string building — safe at module load and
 * in tests (no DOM, no canvas).
 */
export function buildBoardPhoto(scene: WhiteboardScene): string {
  const byId = new Map(scene.nodes.map((n) => [n.id, n] as const));
  const parts: string[] = [];

  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${PHOTO_W} ${PHOTO_H}" font-family="'Segoe Print','Bradley Hand','Comic Sans MS',cursive">`,
    // Wall behind the board, board surface, and a marker tray for depth.
    `<rect width="${PHOTO_W}" height="${PHOTO_H}" fill="#E8E6E0"/>`,
    `<rect x="24" y="20" width="${PHOTO_W - 48}" height="${PHOTO_H - 64}" rx="14" fill="#FCFCF9" stroke="#C9C6BD" stroke-width="6"/>`,
    `<rect x="${PHOTO_W / 2 - 160}" y="${PHOTO_H - 34}" width="320" height="12" rx="6" fill="#C9C6BD"/>`,
    `<defs><marker id="ph-arrow" markerWidth="9" markerHeight="9" refX="7" refY="4.5" orient="auto"><path d="M0,0 L9,4.5 L0,9 Z" fill="${INK}"/></marker></defs>`,
  );

  // Containers first, so member shapes draw on top of the grouping box.
  for (const n of scene.nodes) {
    if (!n.isContainer) continue;
    const members = scene.nodes.filter((m) => m.containerId === n.id);
    const centers = (members.length > 0 ? members : [n]).map(nodeCenter);
    const minX = Math.min(...centers.map((c) => c.cx)) - NODE_W / 2 - 28;
    const maxX = Math.max(...centers.map((c) => c.cx)) + NODE_W / 2 + 28;
    const minY = Math.min(...centers.map((c) => c.cy)) - NODE_H / 2 - 40;
    const maxY = Math.max(...centers.map((c) => c.cy)) + NODE_H / 2 + 24;
    parts.push(
      `<rect x="${minX}" y="${minY}" width="${maxX - minX}" height="${maxY - minY}" rx="18" fill="none" stroke="${INK}" stroke-width="3.5" stroke-dasharray="14 10"/>`,
      `<text x="${minX + 18}" y="${minY + 30}" font-size="24" fill="${INK}">${escapeXml(n.text)}</text>`,
    );
  }

  // Connectors under the shapes, like ink drawn between stickies.
  scene.edges.forEach((e) => {
    const s = byId.get(e.sourceId);
    const t = byId.get(e.targetId);
    if (!s || !t) return;
    const a = nodeCenter(s);
    const b = nodeCenter(t);
    const seg = trimSegment(a.cx, a.cy, b.cx, b.cy, NODE_W / 2 - 24);
    const dashed = e.kind === 'dashed_arrow' || e.kind === 'dashed_line';
    const arrow = e.kind === 'arrow' || e.kind === 'dashed_arrow' || e.kind === 'double_arrow';
    parts.push(
      `<line x1="${seg.x1}" y1="${seg.y1}" x2="${seg.x2}" y2="${seg.y2}" stroke="${INK}" stroke-width="3.5"${
        dashed ? ' stroke-dasharray="12 9"' : ''
      }${arrow ? ' marker-end="url(#ph-arrow)"' : ''}${e.kind === 'double_arrow' ? ' marker-start="url(#ph-arrow)"' : ''}/>`,
    );
    const mark = e.symbol || e.label;
    if (mark) {
      const mx = (seg.x1 + seg.x2) / 2;
      const my = (seg.y1 + seg.y2) / 2 - 10;
      parts.push(
        `<rect x="${mx - 46}" y="${my - 20}" width="92" height="28" fill="#FCFCF9" opacity="0.9"/>`,
        `<text x="${mx}" y="${my}" font-size="20" fill="${INK}" text-anchor="middle">${escapeXml(mark)}</text>`,
      );
    }
  });

  // Shapes: stickies get color + a small deterministic tilt, boxes get marker outlines.
  scene.nodes.forEach((n, i) => {
    if (n.isContainer) return;
    const { cx, cy } = nodeCenter(n);
    const x = cx - NODE_W / 2;
    const y = cy - NODE_H / 2;
    const tilt = ((i % 5) - 2) * 1.4;
    const fill = PHOTO_STICKY_FILL[n.color] ?? 'none';
    const isSticky = n.shape === 'sticky' && fill !== 'none';
    parts.push(`<g transform="rotate(${tilt} ${cx} ${cy})">`);
    if (isSticky) {
      parts.push(
        `<rect x="${x + 4}" y="${y + 6}" width="${NODE_W}" height="${NODE_H}" fill="#00000022"/>`,
        `<rect x="${x}" y="${y}" width="${NODE_W}" height="${NODE_H}" fill="${fill}"/>`,
      );
    } else {
      parts.push(
        `<rect x="${x}" y="${y}" width="${NODE_W}" height="${NODE_H}" rx="10" fill="${
          fill === 'none' ? '#FCFCF9' : fill
        }" stroke="${INK}" stroke-width="3.5"/>`,
      );
    }
    const lines = wrapText(n.text, 18, 3);
    const startY = cy - ((lines.length - 1) * 24) / 2 + 7;
    lines.forEach((line, li) => {
      parts.push(
        `<text x="${cx}" y="${startY + li * 24}" font-size="21" fill="${INK}" text-anchor="middle">${escapeXml(line)}</text>`,
      );
    });
    parts.push('</g>');
  });

  parts.push('</svg>');
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(parts.join(''))}`;
}

// ── The sample scenes ────────────────────────────────────────────────────────

const strategyWallScene: WhiteboardScene = {
  title: 'Q3 Growth Strategy',
  summary: 'A growth goal at the top, the risks and blockers beneath it, and the plays meant to unlock them.',
  nodes: [
    { id: 's1', text: 'Grow enterprise revenue 20%', shape: 'sticky', color: 'yellow', position: { x: 0.5, y: 0.08 }, confidence: 0.96, isContainer: false, containerId: null },
    { id: 's2', text: 'Land 3 lighthouse accounts', shape: 'sticky', color: 'green', position: { x: 0.2, y: 0.32 }, confidence: 0.94, isContainer: false, containerId: null },
    { id: 's3', text: 'Onboarding takes 6 weeks', shape: 'sticky', color: 'red', position: { x: 0.76, y: 0.3 }, confidence: 0.92, isContainer: false, containerId: null },
    { id: 's4', text: 'Pricing page confusion', shape: 'sticky', color: 'orange', position: { x: 0.48, y: 0.46 }, confidence: 0.52, isContainer: false, containerId: null },
    { id: 's5', text: 'Self-serve onboarding revamp', shape: 'sticky', color: 'blue', position: { x: 0.72, y: 0.66 }, confidence: 0.95, isContainer: false, containerId: 'c1' },
    { id: 's6', text: 'SSO and audit logs', shape: 'sticky', color: 'blue', position: { x: 0.36, y: 0.68 }, confidence: 0.97, isContainer: false, containerId: null },
    { id: 's7', text: 'Case study program', shape: 'sticky', color: 'green', position: { x: 0.12, y: 0.62 }, confidence: 0.9, isContainer: false, containerId: null },
    { id: 's8', text: 'Hire 2 solutions engineers', shape: 'sticky', color: 'purple', position: { x: 0.9, y: 0.82 }, confidence: 0.93, isContainer: false, containerId: 'c1' },
    { id: 'c1', text: 'Enablement', shape: 'box', color: 'unknown', position: { x: 0.81, y: 0.74 }, confidence: 0.98, isContainer: true, containerId: null },
  ] satisfies WhiteboardNode[],
  edges: [
    { id: 'e1', sourceId: 's2', targetId: 's1', kind: 'arrow' },
    { id: 'e2', sourceId: 's3', targetId: 's1', kind: 'line', symbol: 'blocks' },
    { id: 'e3', sourceId: 's5', targetId: 's3', kind: 'dashed_arrow' },
    { id: 'e4', sourceId: 's6', targetId: 's2', kind: 'arrow' },
    { id: 'e5', sourceId: 's7', targetId: 's2', kind: 'line' },
  ] satisfies WhiteboardEdge[],
  warnings: [],
  unrecognized: [],
};

const launchChainScene: WhiteboardScene = {
  title: 'Launch Dependencies',
  summary: 'The dependency chain from shipping the v2 API through to the public launch.',
  nodes: [
    { id: 'l1', text: 'Ship v2 API', shape: 'box', color: 'unknown', position: { x: 0.14, y: 0.28 }, confidence: 0.97, isContainer: false, containerId: null },
    { id: 'l2', text: 'Migrate billing', shape: 'box', color: 'unknown', position: { x: 0.46, y: 0.44 }, confidence: 0.95, isContainer: false, containerId: null },
    { id: 'l3', text: 'Public launch', shape: 'sticky', color: 'yellow', position: { x: 0.82, y: 0.18 }, confidence: 0.98, isContainer: false, containerId: null },
    { id: 'l4', text: 'Docs refresh', shape: 'sticky', color: 'blue', position: { x: 0.66, y: 0.72 }, confidence: 0.94, isContainer: false, containerId: null },
    { id: 'l5', text: 'Beta feedback round', shape: 'sticky', color: 'green', position: { x: 0.24, y: 0.72 }, confidence: 0.92, isContainer: false, containerId: null },
  ] satisfies WhiteboardNode[],
  edges: [
    { id: 'e1', sourceId: 'l1', targetId: 'l2', kind: 'arrow' },
    { id: 'e2', sourceId: 'l2', targetId: 'l3', kind: 'arrow' },
    { id: 'e3', sourceId: 'l4', targetId: 'l3', kind: 'dashed_arrow' },
    { id: 'e4', sourceId: 'l5', targetId: 'l2', kind: 'line' },
  ] satisfies WhiteboardEdge[],
  warnings: [],
  unrecognized: [],
};

export const DEMO_BOARDS: DemoBoard[] = [
  {
    id: 'strategy-wall',
    label: 'Strategy wall',
    description:
      '9 stickies with colors, a container, and symbol-labeled arrows — shows category, altitude, containment, and review flags.',
    scene: strategyWallScene,
    photoDataUrl: buildBoardPhoto(strategyWallScene),
  },
  {
    id: 'launch-chain',
    label: 'Dependency chain',
    description: 'A small launch plan drawn as boxes and arrows — shows directed dependencies and prerequisites.',
    scene: launchChainScene,
    photoDataUrl: buildBoardPhoto(launchChainScene),
  },
];
